/**
 * GTM Surface Layer — React Hook
 *
 * Returns venue-context-bound tracking functions.
 * Uses RestaurantGuardContext to build VenueContext automatically.
 */

import { useContext, useMemo } from 'react';
import { RestaurantGuardContext } from '../../../routes/RestaurantGuard';
import { toISO4217 } from '../../currency';
import { VenueContext } from './types';
import {
  emitPageView,
  emitSearch,
  emitViewItemList,
  emitViewItem,
  emitSelectItem,
  emitAddToCart,
  emitBeginCheckout,
  emitAddPaymentInfo,
  emitPurchase,
  emitBookingConfirmation
} from './events';
import type {
  SurfacePageViewParams,
  SurfaceSearchParams,
  SurfaceViewItemListParams,
  SurfaceViewItemParams,
  SurfaceSelectItemParams,
  SurfaceAddToCartParams,
  SurfaceBeginCheckoutParams,
  SurfaceAddPaymentInfoParams,
  SurfacePurchaseParams,
  SurfaceBookingConfirmationParams
} from './types';

export function useGTMAnalytics() {
  const context = useContext(RestaurantGuardContext);

  const venue: VenueContext = useMemo(() => {
    if (!context?.restaurant) {
      return { venueId: '', venueName: '', currency: '' };
    }
    const r = context.restaurant;
    return {
      venueId: r.id,
      venueName: r.name,
      currency: toISO4217(r.currency) ?? '' // Normalize to uppercase ISO 4217
    };
  }, [context?.restaurant]);

  return useMemo(() => ({
    pageView:       (params: SurfacePageViewParams) => emitPageView(venue, params),
    search:         (params: SurfaceSearchParams) => emitSearch(venue, params),
    viewItemList:   (params: SurfaceViewItemListParams) => emitViewItemList(venue, params),
    viewItem:       (params: SurfaceViewItemParams) => emitViewItem(venue, params),
    selectItem:     (params: SurfaceSelectItemParams) => emitSelectItem(venue, params),
    addToCart:      (params: SurfaceAddToCartParams) => emitAddToCart(venue, params),
    beginCheckout:  (params: SurfaceBeginCheckoutParams) => emitBeginCheckout(venue, params),
    addPaymentInfo: (params: SurfaceAddPaymentInfoParams) => emitAddPaymentInfo(venue, params),
    purchase:       (params: SurfacePurchaseParams) => emitPurchase(venue, params),
    bookingConfirmation: (params: SurfaceBookingConfirmationParams) => emitBookingConfirmation(venue, params),
    venue
  }), [venue]);
}
